<script setup>
import { Head, usePage } from '@inertiajs/inertia-vue3';
import { computed } from 'vue';

const page = usePage();

// DICCIONARIO CENTRALIZADO: Modifica aquí los textos de todo el sitio
const pageTitles = {
    'index': 'Inicio',
    'brands': 'Nuestras Marcas',
    'checkout': 'Finalizar Compra',
    'companyinfo': 'Sobre Nosotros',
    'complaintsbook': 'Libro de Reclamaciones',
    'contactinfo': 'Contáctanos',
    'deliverytypes': 'Métodos de Envío',
    'legalbases': 'Bases Legales',
    'paymentmethods': 'Formas de Pago',
    'services': 'Nuestros Servicios',
    'shoppingcart': 'Carrito de Compras',
    'termsconditions': 'Términos y Condiciones',
    'warrantypolicies': 'Políticas de Garantía',
    'category': 'Categoría', // Para la página de categorías
};

const metaData = computed(() => {
    try {
        const props = page.props.value || page.props;
        const url = page.url.value || page.url || '';
        const company = props?.confGlobal?.COMPANY_NAME_SHORT || 'Unens Ecommerce';
        
        // Imagen por defecto (Ruta absoluta para redes sociales)
        //const defaultImage = `${window.location.origin}/img/logo-meta.jpg`;  
        const defaultImage = `${window.location.origin}/documents/mycompany/logos/logo-meta.jpg`;
        // 1. PRIORIDAD MÁXIMA: Si el controlador envía 'label' (ej. Productos o Categorías con nombre)
        if (props.seo && props.seo.label) {
            return {
                title: props.seo.label,
                description: props.seo.description || `Encuentra los mejores productos en ${company}`,
                image: props.seo.image || defaultImage,
                imageAlt: props.seo.image_alt || props.seo.label,
                url: props.seo.url || window.location.href,
                favicon: props?.confGlobal?.COMPANY_FAVICON_PATH || '/documents/mycompany/logos/favicon.png'
            };
        }

        // 2. LÓGICA POR LLAVE (page_name): Centralización desde el controlador
        const pageKey = props.seo?.page_name;
        const pageNameFromDict = pageTitles[pageKey];

        if (pageNameFromDict) {
            return {
                title: `${pageNameFromDict} | ${company}`,
                description: props.seo?.description || `Sección de ${pageNameFromDict} en nuestra tienda ${company}.`,
                image: props.seo?.image || defaultImage,
                imageAlt: `${pageNameFromDict} - ${company}`,
                url: window.location.href,
                favicon: props?.confGlobal?.COMPANY_FAVICON_PATH || '/documents/mycompany/logos/favicon.png'
            };
        }

        // 3. LÓGICA POR URL (Respaldo si no hay page_name)
        const urlString = url?.toString() || ''; 
        const pathSegments = urlString.split('/').filter(s => s && s.length > 0) || [];

        // Usamos optional chaining para mayor seguridad
        const lastSegment = pathSegments.length > 0 
            ? pathSegments[pathSegments.length - 1].split('?')[0] 
            : 'inicio';
        const pageNameByUrl = pageTitles[lastSegment];

        if (pageNameByUrl) {
            return {
                title: `${pageNameByUrl} | ${company}`,
                description: `Explora ${pageNameByUrl} en la plataforma oficial de ${company}.`,
                image: defaultImage,
                imageAlt: `${pageNameByUrl} - ${company}`,
                url: window.location.href,
                favicon: props?.confGlobal?.COMPANY_FAVICON_PATH || '/documents/mycompany/logos/favicon.png'
            };
        }

        // 4. INICIO O FALLBACK CRÍTICO
        return {
            title: company,
            description: `Bienvenido a nuestra tienda tecnológica ${company}. Encuentra lo mejor en hardware y software.`,
            image: defaultImage,
            imageAlt: `Logotipo de ${company}`,
            url: window.location.href,
            favicon: props?.confGlobal?.COMPANY_FAVICON_PATH || '/documents/mycompany/logos/favicon.png'
        };

    } catch (e) {
        console.error("Error en SeoMeta:", e);
        return {
            title: 'Unens Ecommerce',
            description: 'Tu tienda tecnológica online.',
            image: '', 
            imageAlt: '',
            url: '',
            favicon: '/documents/mycompany/logos/favicon.png'
        };
    }
});
</script>

<template>
    <Head>
        <title>{{ metaData.title }}</title>
        <meta name="description" :content="metaData.description" />
        <link rel="canonical" :href="metaData.url" />

        <meta property="og:type" content="website" />
        <meta property="og:url" :content="metaData.url" />
        <meta property="og:title" :content="metaData.title" />
        <meta property="og:description" :content="metaData.description" />
        <meta property="og:image" :content="metaData.image" />
        <meta property="og:image:secure_url" :content="metaData.image" />
        <meta property="og:image:alt" :content="metaData.imageAlt" />
        <meta property="og:image:width" content="630" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:site_name" content="Unens Ecommerce" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" :content="metaData.title" />
        <meta name="twitter:description" :content="metaData.description" />
        <meta name="twitter:image" :content="metaData.image" />
        
        <link rel="icon" type="image/png" :href="metaData.favicon" />
    </Head>
</template>